import Button from "@/components/button"
import SearchBar from "@/components/search-bar"
import Space from "@/components/space"
import { useAppSelector } from "@/core/root-reducer"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import mapStore from "@/entities/map/model/map.store"
import populationStore from "@/entities/population/model/population.store"
import { Population } from "@/entities/population/model/population.types"
import UnitChip from "@/components/UnitChip"
import siteStore from "@/entities/site/model/site.store"
import { Site } from "@/entities/site/model/site.types"
import { faLocationDot, faUserFriends } from "@fortawesome/free-solid-svg-icons"
import { useMemo, useState } from "react"

export type PhysicalRiskUnitContext =
  { type: "establishment" } | { type: "site"; site: Site } | { type: "population"; population: Population }

interface Props {
  isMulti?: boolean
  onAddUnit?: (unit: PhysicalRiskUnitContext) => void
  onRemoveUnit?: (unit: PhysicalRiskUnitContext) => void
  onSelect: (unit: PhysicalRiskUnitContext) => void
  selectedUnits?: PhysicalRiskUnitContext[]
  selectedUnit?: PhysicalRiskUnitContext
  unitType?: "all" | "population" | "site"
}

function normalizeSearch(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
}

export default function PhysicalRiskUnitsSelector({
  isMulti,
  onAddUnit,
  onRemoveUnit,
  onSelect,
  selectedUnit,
  selectedUnits = [],
  unitType = "all"
}: Props) {
  const activeEstablishment = useAppSelector(establishmentStore.selectors.active)
  const maps = useAppSelector(mapStore.selectors.list)
  const sites = useAppSelector(siteStore.selectors.listForActiveEstablishment)
  const populations = useAppSelector(populationStore.selectors.listForActiveEstablishment)
  const [searchText, setSearchText] = useState("")
  const [filter, setFilter] = useState<"all" | "population" | "site">(unitType)

  const filteredSites = useMemo(() => {
    const search = normalizeSearch(searchText)
    if (unitType === "population" || filter === "population") return []
    return sites.filter((site) => !search || normalizeSearch(site.name).includes(search))
  }, [filter, searchText, sites, unitType])

  const filteredPopulations = useMemo(() => {
    const search = normalizeSearch(searchText)
    if (unitType === "site" || filter === "site") return []
    return populations.filter((population) => !search || normalizeSearch(population.name).includes(search))
  }, [filter, populations, searchText, unitType])

  const siteMapIds = new Set(sites.map((site) => site.MapId).filter(Boolean))
  const hasMultipleMaps = siteMapIds.size > 1
  const siteGroups = hasMultipleMaps
    ? maps
        .filter((map) => siteMapIds.has(map.id))
        .sort((a, b) => a.order - b.order)
        .map((map) => ({ id: map.id, name: map.name, sites: filteredSites.filter((site) => site.MapId === map.id) }))
    : [{ id: "all", name: "", sites: filteredSites }]
  if (hasMultipleMaps) {
    siteGroups.push({
      id: "without-map",
      name: "Sans niveau",
      sites: filteredSites.filter((site) => !maps.some((map) => map.id === site.MapId))
    })
  }

  const isSecondarySelectionEnabled = Boolean(onAddUnit || onRemoveUnit)
  const isEstablishmentSelected = !isSecondarySelectionEnabled && selectedUnit?.type === "establishment"
  const isUnitSelected = (unit: PhysicalRiskUnitContext) => {
    if (!isMulti && !isSecondarySelectionEnabled) return false
    return selectedUnits.some((selected) => {
      if (selected.type !== unit.type) return false
      if (selected.type === "establishment") return true
      if (selected.type === "site" && unit.type === "site") return selected.site.id === unit.site.id
      if (selected.type === "population" && unit.type === "population")
        return selected.population.id === unit.population.id
      return false
    })
  }
  const isSecondaryActionEnabled = isMulti || isSecondarySelectionEnabled

  function getAddClick(unit: PhysicalRiskUnitContext) {
    if (!isSecondaryActionEnabled || isUnitSelected(unit)) return undefined
    return () => (onAddUnit ? onAddUnit(unit) : onSelect(unit))
  }

  function getRemoveClick(unit: PhysicalRiskUnitContext) {
    if (!isSecondaryActionEnabled || !isUnitSelected(unit)) return undefined
    return () => (onRemoveUnit ? onRemoveUnit(unit) : onSelect(unit))
  }

  function isPrimarySelected(unit: PhysicalRiskUnitContext) {
    if (isSecondarySelectionEnabled) return false
    if (unit.type === "site") return selectedUnit?.type === "site" && selectedUnit.site.id === unit.site.id
    if (unit.type === "population")
      return selectedUnit?.type === "population" && selectedUnit.population.id === unit.population.id
    return selectedUnit?.type === "establishment"
  }

  return (
    <div className="flex flex-col gap-5 pb-4">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <SearchBar searchText={searchText} onSearch={setSearchText} />
        </div>
        {unitType === "all" && (
          <>
            <Space />
            <Button
              isWithBorder
              className={filter === "population" ? "primary" : ""}
              icon={faUserFriends}
              onClick={() => setFilter(filter === "population" ? "all" : "population")}>
              Fonctions
            </Button>
            <Button
              isWithBorder
              className={filter === "site" ? "primary" : ""}
              icon={faLocationDot}
              onClick={() => setFilter(filter === "site" ? "all" : "site")}>
              Sites
            </Button>
          </>
        )}
      </div>

      <div className="flex min-h-[120px] flex-wrap content-start gap-[10px] pr-10">
        {unitType === "all" && (
          <UnitChip
            activeMultiple={isSecondaryActionEnabled}
            hideIcon
            isActive={isEstablishmentSelected || isUnitSelected({ type: "establishment" })}
            onAddClick={getAddClick({ type: "establishment" })}
            onClick={() => onSelect({ type: "establishment" })}
            onRemoveClick={getRemoveClick({ type: "establishment" })}
            unit={{ type: "establishment", name: activeEstablishment.name }}
          />
        )}

        {siteGroups.filter((group) => group.sites.length > 0).map((group) => (
          <div key={group.id} className={hasMultipleMaps ? "flex w-full flex-col gap-[10px]" : "contents"}>
            {hasMultipleMaps && <div className="grey-t text-base font-bold">{group.name}</div>}
            <div className={hasMultipleMaps ? "flex flex-wrap gap-[10px]" : "contents"}>
              {group.sites.map((site) => {
                const unit = { type: "site" as const, site }
                return (
                  <UnitChip
                    activeMultiple={isSecondaryActionEnabled}
                    hideIcon
                    key={site.id}
                    isActive={isPrimarySelected(unit) || isUnitSelected(unit)}
                    onAddClick={getAddClick(unit)}
                    onClick={() => onSelect(unit)}
                    onRemoveClick={getRemoveClick(unit)}
                    unit={unit}
                  />
                )
              })}
            </div>
          </div>
        ))}

        {filteredPopulations.map((population) => {
          const unit = { type: "population" as const, population }
          return (
            <UnitChip
              activeMultiple={isSecondaryActionEnabled}
              hideIcon
              key={population.id}
              isActive={isPrimarySelected(unit) || isUnitSelected(unit)}
              onAddClick={getAddClick(unit)}
              onClick={() => onSelect(unit)}
              onRemoveClick={getRemoveClick(unit)}
              unit={unit}
            />
          )
        })}

        {filteredSites.length === 0 && filteredPopulations.length === 0 && (
          <div className="grey-t m-auto">Aucune unité de travail trouvée.</div>
        )}
      </div>
    </div>
  )
}
